import React, { useEffect, useId, useRef, useState } from 'react';
import { AiMode, Overview, getAiSwitch, setAiSwitch } from '../../../api/cafAutomation';
import { formatAgo } from '../format';
import { Modal } from '../Overlay';
import { runSave } from '../saveEngine';
import { useToast } from '../ToastContext';
import { MODE_LABEL, MODE_SENTENCE, SWITCH_SAVED, changedLine, liveLine, readinessSentence, switchConfirm } from './automationCopy';
import { NoteField, SaveProblem } from './formBits';

const MODES: AiMode[] = ['off', 'watching', 'on'];

/** The one AI switch (Off, Watching, On) with one sentence, the live line and who last changed it. */
export const AiSwitchCard: React.FC<{ overview: Overview; onRequest: (mode: AiMode) => void }> = ({ overview, onRequest }) => {
  const mode = overview.switch.mode;
  const canTurnOn = Boolean(overview.capabilities?.runs && overview.activation);
  const changed = changedLine(overview.switch.changed, overview.switch.legacy);
  const last = overview.live.last_decided_at ? formatAgo(overview.live.last_decided_at) : null;
  return (
    <section className="ui-card" aria-label="The AI">
      <div className="ui-card__header">
        <h2 className="ui-card__title">The AI</h2>
        <span className="cur-muted cur-small">One switch for all AI processing of lender emails</span>
      </div>
      <div className="ui-card__body">
        <div className="cur-switch-row">
          <div className="cur-switch" role="group" aria-label="The AI switch">
            {MODES.map((value) => (
              <button key={value} type="button" aria-pressed={mode === value}
                disabled={value === 'on' && mode !== 'on' && !canTurnOn}
                onClick={() => { if (value !== mode) onRequest(value); }}>
                <span className={`cur-dot cur-dot--${value}`} aria-hidden="true" />{MODE_LABEL[value]}
              </button>
            ))}
          </div>
          <div className="cur-grow">
            <p>{MODE_SENTENCE[mode]}</p>
            <div className="cur-live">
              <span className={`cur-dot cur-dot--${mode}`} aria-hidden="true" />
              {liveLine(mode, overview.live.waiting, last, overview.workflows_paused)}
            </div>
            {changed && <div className="cur-changed">{changed}</div>}
            {!canTurnOn && mode !== 'on' && <p className="cur-hint">The server is not ready to run replacement workflows.</p>}
            {!overview.capabilities?.runs && (overview.capabilities?.blockers?.length ?? 0) > 0 &&
              <ul className="cur-hint">{Array.from(new Set(overview.capabilities?.blockers?.map(readinessSentence))).map((line) => <li key={line}>{line}</li>)}</ul>}
          </div>
        </div>
      </div>
    </section>
  );
};

interface ConfirmProps {
  overview: Overview;
  to: AiMode;
  onClose: () => void;
  onSaved: () => void;
  onReload: () => void;
  onSeeWorkflows: () => void;
}

/** Asks before changing the switch. The note is optional; a stale page is refused (never overwritten). */
export const SwitchConfirm: React.FC<ConfirmProps> = ({ overview, to, onClose, onSaved, onReload, onSeeWorkflows }) => {
  const id = useId();
  const toast = useToast();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [reloadPending, setReloadPending] = useState(false);
  const [problem, setProblem] = useState('');
  const reloadFrom = useRef<Overview | null>(null);
  const epoch = overview.switch.epoch;
  const canTurnOn = Boolean(overview.capabilities?.runs && overview.activation);
  const copy = switchConfirm({
    from: overview.switch.mode, to, workflowsOn: overview.workflows_on,
    waiting: overview.activation?.waiting_eligible ?? overview.live.waiting,
    watched: overview.activation?.watched_eligible,
    replacement: canTurnOn,
    paused: overview.workflows_paused,
  });

  // A failed refresh leaves the old overview object in place. Only a fresh
  // source response may unlock another compare-and-set attempt.
  useEffect(() => {
    if (reloadPending && reloadFrom.current !== overview) {
      setReloadPending(false);
      setProblem('');
    }
  }, [overview, reloadPending]);

  const save = async () => {
    if (saving || reloadPending || (to === 'on' && !canTurnOn)) return;
    setSaving(true);
    setProblem('');
    const result = await runSave(
      () => setAiSwitch({ mode: to, expected_epoch: epoch, ...(note.trim() ? { note: note.trim() } : {}) }),
      {
        // A lost reply: the switch's own epoch says whether our change landed.
        readBack: async () => {
          const current = await getAiSwitch();
          if (current.epoch === epoch + 1 && current.mode === to) return { state: 'landed', value: current };
          if (current.epoch === epoch) return { state: 'not_landed' };
          return { state: 'unknown' };
        },
      },
    );
    setSaving(false);
    if (!result.ok) {
      setProblem(result.message);
      return;
    }
    toast.saved(SWITCH_SAVED[to]);
    onSaved();
    onClose();
  };

  return (
    <Modal title={copy.title} onClose={onClose} busy={saving}
      footer={(
        <>
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className={`ui-btn ui-btn--${copy.danger ? 'danger' : 'primary'} ui-btn--md`}
            disabled={saving || reloadPending || (to === 'on' && !canTurnOn)} onClick={() => { void save(); }}>
            {saving ? <><span className="cur-spin" aria-hidden="true" />Saving…</> : copy.button}
          </button>
        </>
      )}>
      {copy.lines.map((line) => <p key={line}>{line}</p>)}
      {to === 'on' && (
        <div><button type="button" className="cur-linkish" onClick={onSeeWorkflows}>See the workflows</button></div>
      )}
      <SaveProblem message={problem} onReload={() => { reloadFrom.current = overview; setReloadPending(true); onReload(); }} />
      <NoteField id={`${id}-note`} value={note} onChange={setNote} disabled={saving} />
    </Modal>
  );
};
